/** @format */

import {
  Alert,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { WarningTwoTone } from "@ant-design/icons";
import moment, { Moment } from "moment";
import React, { FC, useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../../../constants/api";
import { TDailyWorkTypes } from "../../../../redux-modules/declarations/maps/auth";
import apiClient from "../../../../utils/apiClient";
import { showNotification } from "../../../../utils/showNotification";
import { UIButton } from "../../../ui/Button";

const { Option } = Select;

/**
 * Fase 3: storico dei contratti dell'utente.
 *
 * Ogni versione vale dalla sua data fino al giorno prima della successiva. I calcoli (banca ore,
 * export, contatori, regola 9,5) usano il contratto in vigore nel giorno; i campi di
 * "Informazioni Professionali" mostrano quello di oggi.
 */
export interface TContractVersion {
  id: number;
  user_id: number;
  valido_dal: string;
  valido_al: string | null;
  in_vigore: boolean;
  futuro: boolean;
  daily_hours: number | null;
  contract_weekly_hours: number | null;
  contract_weekly_days: number | null;
  is_pausapranzo_enabled: boolean;
  pausapranzo_hours: string | null;
  is_single_badge: boolean;
  is_forfettizzato: boolean;
  daily_work_type_id: number | null;
  daily_work_type: string | null;
  note: string | null;
  created_by: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface TContractWarning {
  user_id?: number;
  codice: string;
  livello: string;
  dal: string;
  al: string;
  messaggio: string;
}

interface Props {
  user_id: string;
  dailyWorkTypes: TDailyWorkTypes[];
  canEdit: boolean;
  canManageForfettizzato: boolean;
  /** dopo una modifica: il profilo va ricaricato (i campi di oggi possono essere cambiati) */
  onChanged?: () => void;
}

const formatDate = (date: string | null): string => (date ? moment(date).format("DD/MM/YYYY") : "");
const formatNumber = (value: number | null): string =>
  value === null || value === undefined ? "–" : String(value).replace(".", ",");

export const ContractTab: FC<Props> = ({
  user_id,
  dailyWorkTypes,
  canEdit,
  canManageForfettizzato,
  onChanged,
}: Props): JSX.Element => {
  const [versions, setVersions] = useState<TContractVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<TContractVersion | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [warningsMonth, setWarningsMonth] = useState<Moment>(moment());
  const [warnings, setWarnings] = useState<TContractWarning[]>([]);
  const [isWarningsLoading, setIsWarningsLoading] = useState(false);
  const [form] = Form.useForm();
  const pausaPranzo = Form.useWatch("is_pausapranzo_enabled", form);

  const baseUrl = `${API_ENDPOINTS.USERS_LIST}/${user_id}${API_ENDPOINTS.CONTRACTS}`;

  const fetchVersions = (): void => {
    setIsLoading(true);
    apiClient
      .get(baseUrl)
      .then(res => setVersions(res.data.data))
      .catch(err =>
        showNotification(
          "error",
          "Errore",
          err?.response?.data?.message ?? "Contratti non disponibili",
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const fetchWarnings = (month: Moment): void => {
    setIsWarningsLoading(true);
    apiClient
      .get(`${baseUrl}/avvisi`, {
        params: {
          date_from: month.clone().startOf("month").format("YYYY-MM-DD"),
          date_to: month.clone().endOf("month").format("YYYY-MM-DD"),
        },
      })
      .then(res => setWarnings(res.data.data))
      .catch(() => setWarnings([]))
      .finally(() => setIsWarningsLoading(false));
  };

  useEffect(() => {
    fetchVersions();
  }, [user_id]);

  useEffect(() => {
    fetchWarnings(warningsMonth);
  }, [user_id, warningsMonth]);

  const openModal = (version: TContractVersion | null): void => {
    const base = version ?? versions.find(v => v.in_vigore) ?? null;
    form.resetFields();
    form.setFieldsValue({
      valido_dal: version ? moment(version.valido_dal) : moment().add(1, "day"),
      daily_hours: base?.daily_hours ?? null,
      contract_weekly_hours: base?.contract_weekly_hours ?? null,
      contract_weekly_days: base?.contract_weekly_days ?? null,
      daily_work_type_id: base?.daily_work_type_id ?? "standard",
      is_pausapranzo_enabled: base?.is_pausapranzo_enabled ?? false,
      pausapranzo_hours: base?.pausapranzo_hours ? parseFloat(base.pausapranzo_hours) : null,
      is_single_badge: base?.is_single_badge ?? false,
      is_forfettizzato: base?.is_forfettizzato ?? false,
      note: version ? version.note : null,
    });
    setEditing(version);
    setIsModalOpen(true);
  };

  const save = (): void => {
    form.validateFields().then(values => {
      const payload = {
        ...values,
        valido_dal: (values.valido_dal as Moment).format("YYYY-MM-DD"),
        is_pausapranzo_enabled: values.is_pausapranzo_enabled ? 1 : 0,
        is_single_badge: values.is_single_badge ? 1 : 0,
        is_forfettizzato: values.is_forfettizzato ? 1 : 0,
      };
      setIsSaving(true);
      const call = editing
        ? apiClient.put(`${API_ENDPOINTS.CONTRACTS}/${editing.id}`, payload)
        : apiClient.post(baseUrl, payload);
      call
        .then(res => {
          const ricalcolate = res.data.timebank_ricalcolate ?? 0;
          showNotification(
            "success",
            res.data.message ?? "Contratto salvato",
            ricalcolate > 0
              ? `Ricalcolate ${ricalcolate} righe di banca ore dei giorni interessati.`
              : "",
          );
          setIsModalOpen(false);
          fetchVersions();
          fetchWarnings(warningsMonth);
          onChanged && onChanged();
        })
        .catch(err =>
          showNotification(
            "error",
            "Errore",
            err?.response?.data?.message ?? "Salvataggio non riuscito",
          ),
        )
        .finally(() => setIsSaving(false));
    });
  };

  const remove = (version: TContractVersion): void => {
    apiClient
      .delete(`${API_ENDPOINTS.CONTRACTS}/${version.id}`)
      .then(res => {
        showNotification("success", "Contratto", res.data.message);
        fetchVersions();
        fetchWarnings(warningsMonth);
        onChanged && onChanged();
      })
      .catch(err =>
        showNotification(
          "error",
          "Errore",
          err?.response?.data?.message ?? "Eliminazione non riuscita",
        ),
      );
  };

  const columns = [
    {
      title: "Periodo",
      key: "periodo",
      render: function renderPeriodo(_: unknown, v: TContractVersion): JSX.Element {
        return (
          <Space direction="vertical" size={0}>
            <span>
              dal <strong>{formatDate(v.valido_dal)}</strong>
              {v.valido_al ? (
                <>
                  {" "}
                  al <strong>{formatDate(v.valido_al)}</strong>
                </>
              ) : null}
            </span>
            {v.in_vigore && <Tag color="green">In vigore</Tag>}
            {v.futuro && <Tag color="blue">Futuro</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Ore giornaliere",
      key: "daily_hours",
      render: (_: unknown, v: TContractVersion): string => formatNumber(v.daily_hours),
    },
    {
      title: "Ore / giorni settimanali",
      key: "weekly",
      render: (_: unknown, v: TContractVersion): string =>
        `${formatNumber(v.contract_weekly_hours)} / ${formatNumber(v.contract_weekly_days)}`,
    },
    {
      title: "Tipologia",
      key: "daily_work_type",
      render: (_: unknown, v: TContractVersion): string => v.daily_work_type ?? "Standard",
    },
    {
      title: "Pausa pranzo",
      key: "pausa",
      render: (_: unknown, v: TContractVersion): string =>
        v.is_pausapranzo_enabled
          ? `${formatNumber(v.pausapranzo_hours ? parseFloat(v.pausapranzo_hours) : null)} h`
          : "No",
    },
    {
      title: "Altro",
      key: "flags",
      render: function renderFlags(_: unknown, v: TContractVersion): JSX.Element {
        return (
          <>
            {v.is_single_badge && <Tag>Single badge</Tag>}
            {v.is_forfettizzato && <Tag>Forfettizzato</Tag>}
          </>
        );
      },
    },
    {
      title: "Note",
      key: "note",
      render: function renderNote(_: unknown, v: TContractVersion): JSX.Element {
        return (
          <Tooltip title={v.updated_by ? `Ultima modifica: ${v.updated_by}` : undefined}>
            <span>{v.note}</span>
          </Tooltip>
        );
      },
    },
    ...(canEdit
      ? [
          {
            title: "",
            key: "azioni",
            render: function renderAzioni(_: unknown, v: TContractVersion): JSX.Element {
              return (
                <Space>
                  <UIButton size="small" onClick={(): void => openModal(v)}>
                    Modifica
                  </UIButton>
                  {versions.length > 1 && (
                    <Popconfirm
                      title="Eliminare questa versione? Il periodo passa alla versione precedente."
                      okText="Elimina"
                      cancelText="Annulla"
                      onConfirm={(): void => remove(v)}
                    >
                      <UIButton size="small" danger>
                        Elimina
                      </UIButton>
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Row gutter={[12, 12]} justify="space-between" align="middle">
        <Col>
          <Typography.Text type="secondary">
            Ogni versione vale dalla sua data fino al giorno prima della successiva. Banca ore,
            export e contatori usano il contratto in vigore in ciascun giorno.
          </Typography.Text>
        </Col>
        {canEdit && (
          <Col>
            <UIButton type="primary" onClick={(): void => openModal(null)}>
              Nuova versione
            </UIButton>
          </Col>
        )}
      </Row>
      <Table
        style={{ marginTop: "12px" }}
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={versions}
        columns={columns}
        pagination={false}
      />

      <Row style={{ marginTop: "24px" }} gutter={[12, 12]} align="middle">
        <Col>
          <Typography.Title level={5} style={{ margin: 0 }}>
            <WarningTwoTone twoToneColor="#faad14" /> Elenco Segnalazioni
          </Typography.Title>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            allowClear={false}
            format="MMMM YYYY"
            value={warningsMonth}
            onChange={(value): void => {
              if (value) setWarningsMonth(value);
            }}
          />
        </Col>
      </Row>
      <List
        style={{ marginTop: "8px" }}
        size="small"
        loading={isWarningsLoading}
        dataSource={warnings}
        locale={{ emptyText: "Nessuna segnalazione nel mese" }}
        renderItem={(w: TContractWarning): JSX.Element => <List.Item>{w.messaggio}</List.Item>}
      />

      <Modal
        title={editing ? "Modifica versione del contratto" : "Nuova versione del contratto"}
        visible={isModalOpen}
        onCancel={(): void => setIsModalOpen(false)}
        onOk={save}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={isSaving}
        destroyOnClose
        width={640}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: "16px" }}
          message={
            editing
              ? "La modifica vale per tutto il periodo di questa versione. Se tocca giorni passati, la banca ore di quei giorni viene ricalcolata (i mesi bloccati restano invariati)."
              : "La nuova versione vale dalla data indicata fino alla versione successiva. Per una data passata, la banca ore di quei giorni viene ricalcolata (i mesi bloccati restano invariati)."
          }
        />
        <Form form={form} layout="vertical" component={false}>
          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item
                name="valido_dal"
                label="In vigore dal"
                rules={[{ required: true, message: "Indica la data" }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="daily_work_type_id" label="Tipologia di contratto">
                <Select>
                  <Option value="standard">Standard</Option>
                  {dailyWorkTypes?.map(type => (
                    <Option key={type.id} value={Number(type.id)}>
                      {type.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_hours" label="Ore giornaliere">
                <InputNumber
                  min={0}
                  max={24}
                  step={0.5}
                  decimalSeparator=","
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contract_weekly_hours" label="Ore settimanali">
                <InputNumber
                  min={0}
                  max={168}
                  step={0.5}
                  decimalSeparator=","
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contract_weekly_days" label="Giorni settimanali">
                <InputNumber min={0} max={7} step={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_pausapranzo_enabled"
                label="Calcola pausa pranzo"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col span={8}>
              {pausaPranzo && (
                <Form.Item
                  name="pausapranzo_hours"
                  label="Ore pausa pranzo"
                  rules={[{ required: true, message: "Indica le ore" }]}
                >
                  <InputNumber
                    min={0.5}
                    max={24}
                    step={0.5}
                    decimalSeparator=","
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={8}></Col>
            <Col span={8}>
              <Form.Item name="is_single_badge" label="Singola timbratura" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Col>
            {canManageForfettizzato && (
              <Col span={8}>
                <Form.Item
                  name="is_forfettizzato"
                  label="Straordinario forfettizzato"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item name="note" label="Note">
                <Input maxLength={255} placeholder="Es. passaggio a part time" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
